import React from 'react';
import {
    Box,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    ChangeStatusIcon,
    CTableWrapper,
    DeleteIcon,
    EditIcon,
    OptionalDataIcon,
} from '../../../../../dum';
import {
    formatDate,
    formatFloat,
    formatRelativeDate,
} from '../../../../../../helpers';
import { OrderRequestsPageQuery } from '../../../../../../services';

type OrderRequestDoc =
    OrderRequestsPageQuery['paginatedOrderRequests']['docs'][number];

interface IOrderRequestCard {
    orderRequest: OrderRequestDoc;
    onEdit: (orderRequest: OrderRequestDoc) => void;
    onDelete: (orderRequest: OrderRequestDoc) => void;
    // Optional, admin-gated. The icon only renders when the handler is provided
    // (the list block passes it only for admins), mirroring the context menu.
    onChangeStatus?: (orderRequest: OrderRequestDoc) => void;
    // Optional, sales-gated. Edits the optional fields (notes, entrega estimada)
    // outside the status-1 lock — always enabled when shown, mirroring the sale
    // card's onEditDetails.
    onEditDetails?: (orderRequest: OrderRequestDoc) => void;
}

/**
 * Card body for one order request inside the Pedidos CCardList. Header lines
 * (código/estatus/fecha, then cliente/entrega estimada/notas), and the
 * requested-vs-sold products sub-table with actions.
 */
export function OrderRequestCard({
    orderRequest,
    onEdit,
    onDelete,
    onChangeStatus,
    onEditDetails,
}: IOrderRequestCard) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const subBaseColor = isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff';
    const subStripeColor = isDark ? 'rgba(59,130,246,0.16)' : '#dbeafe';

    // Join each requested product with what has been sold against it.
    const products = orderRequest.order_request_products?.map((orp) => {
        const foundProductSold = orderRequest.order_sale_sold_products?.find(
            (ossp) => ossp.product_id === orp.product_id,
        );
        return {
            ...orp,
            kilos_sold: foundProductSold ? foundProductSold.kilos : 0,
            groups_sold: foundProductSold ? foundProductSold.groups : 0,
            // Server-computed line total (kilo_price·kilos + group_price·groups),
            // so group-priced lines are included.
            total_sold: foundProductSold ? foundProductSold.total : 0,
        };
    });

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 0.5,
                }}
            >
                <Typography variant={'subtitle2'}>
                    #{orderRequest.order_code}
                </Typography>
                {orderRequest.order_request_status ? (
                    <Chip
                        size={'small'}
                        label={orderRequest.order_request_status.name}
                    />
                ) : null}
                <Box sx={{ flex: 1 }} />
                <Typography variant={'body2'}>
                    {formatDate(orderRequest.date)}
                    {orderRequest.updated_at ? (
                        <Typography
                            component={'span'}
                            variant={'caption'}
                            color={'text.secondary'}
                            sx={{ ml: 0.5 }}
                        >
                            (act. {formatRelativeDate(orderRequest.updated_at)}
                            {orderRequest.updated_by
                                ? ` por ${orderRequest.updated_by.fullname}`
                                : ''}
                            )
                        </Typography>
                    ) : null}
                </Typography>
            </Box>
            <Typography variant={'body2'} sx={{ mb: 1 }}>
                <b>{orderRequest.account?.name}</b>
                {orderRequest.estimated_delivery_date
                    ? ` · entrega estimada ${formatDate(
                          orderRequest.estimated_delivery_date,
                      )}`
                    : ''}
            </Typography>
            {orderRequest.notes ? (
                <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                    display={'block'}
                    sx={{ mb: 1 }}
                >
                    {orderRequest.notes}
                </Typography>
            ) : null}
            <CTableWrapper
                padding={false}
                baseColor={subBaseColor}
                stripeColor={subStripeColor}
                items={[
                    {
                        title: 'Producto',
                        cellGrow: 45,
                        content: (orp) => orp.product?.external_description,
                    },
                    {
                        title: 'Bultos',
                        cellGrow: 10,
                        textAlign: 'right',
                        content: (orp) => formatFloat(orp.groups),
                    },
                    {
                        title: 'Kilos',
                        cellGrow: 10,
                        textAlign: 'right',
                        content: (orp) => formatFloat(orp.kilos),
                    },
                    {
                        title: 'Total',
                        cellGrow: 10,
                        textAlign: 'right',
                        content: (orp) => formatFloat(orp.total),
                    },
                    {
                        title: 'Bultos vendidos',
                        cellGrow: 10,
                        textAlign: 'right',
                        content: (orp) => formatFloat(orp.groups_sold),
                    },
                    {
                        title: 'Kilos vendidos',
                        cellGrow: 10,
                        textAlign: 'right',
                        content: (orp) => formatFloat(orp.kilos_sold),
                    },
                    {
                        title: 'Total vendido',
                        cellGrow: 10,
                        textAlign: 'right',
                        content: (orp) => formatFloat(orp.total_sold),
                    },
                ]}
                data={products}
            />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 0.5,
                    mt: 1,
                }}
            >
                <IconButton
                    size={'small'}
                    disabled={!orderRequest.is_editable}
                    onClick={(e) => {
                        // The whole card is clickable (opens edit) — keep the
                        // buttons from double-firing through it.
                        e.stopPropagation();
                        onEdit(orderRequest);
                    }}
                >
                    <EditIcon />
                </IconButton>
                {onEditDetails ? (
                    <Tooltip arrow title={'Editar datos opcionales'}>
                        <IconButton
                            size={'small'}
                            onClick={(e) => {
                                e.stopPropagation();
                                onEditDetails(orderRequest);
                            }}
                        >
                            <OptionalDataIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
                {onChangeStatus ? (
                    <Tooltip arrow title={'Cambiar status'}>
                        <IconButton
                            size={'small'}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChangeStatus(orderRequest);
                            }}
                        >
                            <ChangeStatusIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
                <IconButton
                    size={'small'}
                    disabled={!orderRequest.is_deletable}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(orderRequest);
                    }}
                >
                    <DeleteIcon />
                </IconButton>
            </Box>
        </Box>
    );
}
